import { useEffect, useState } from "react";
import { api, type JarvisConfig } from "../api.js";
import { useAuth } from "../auth.js";
import { useLocale } from "../ui.js";

/**
 * F4A-4 — "STOP SHIKKA EVERYWHERE" mobile page (hash route `#stop`).
 *
 * A full-screen, owner-only big red STOP that flips the server Shikka CEO
 * `emergencyStop` flag. The desktop app polls that flag and stops immediately.
 * Bookmark `…/#stop` on the phone home screen for one-tap access. Resume is a
 * separate confirmed action; nothing here executes on the desktop directly.
 */
export function OwnerStopPage() {
  const { token, isPrivileged, account } = useAuth();
  const { tr } = useLocale();
  const [config, setConfig] = useState<JarvisConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!token) return;
    let live = true;
    void api.jarvisConfig(token).then((res) => {
      if (!live) return;
      if (res.status === 403 || !res.data?.isJarvisAdmin) { setDenied(true); return; }
      if (res.data?.config) setConfig(res.data.config);
    });
    return () => { live = false; };
  }, [token]);

  if (!token || !isPrivileged) {
    return (
      <div className="center-shell">
        <div className="card">{tr("noAccess")}</div>
      </div>
    );
  }

  async function stopNow() {
    if (!token) return;
    setBusy(true);
    const res = await api.jarvisEmergencyStop(token);
    if (res.ok && res.data?.config) setConfig(res.data.config);
    else if (res.status === 403) setDenied(true);
    setBusy(false);
  }
  async function resume() {
    if (!token) return;
    if (!window.confirm(tr("resumeConfirm"))) return;
    setBusy(true);
    const res = await api.jarvisSetConfig(token, { emergencyStop: false });
    if (res.ok && res.data?.config) setConfig(res.data.config);
    setBusy(false);
  }

  if (denied) {
    return (
      <div className="center-shell">
        <div className="card">{tr("noAccess")}</div>
      </div>
    );
  }

  const stopped = config?.emergencyStop ?? false;

  return (
    <div className="owner-stop">
      <div className="owner-stop-head">
        <span className="owner-stop-brand">Smart Shikka</span>
        <span className="owner-stop-who">{account?.email}</span>
      </div>

      <div className={`owner-stop-status ${stopped ? "stopped" : "live"}`}>
        {stopped ? tr("ownerStopActive") : tr("ownerStopIdle")}
      </div>

      <button
        type="button"
        className="owner-stop-btn"
        disabled={busy || stopped}
        onClick={() => void stopNow()}
      >
        ⏹<br />{tr("ownerStopButton")}
      </button>

      {stopped && (
        <button type="button" className="owner-stop-resume" disabled={busy} onClick={() => void resume()}>
          {tr("resumeShikkaCeo")}
        </button>
      )}

      <p className="owner-stop-note">{tr("ownerStopNote")}</p>
      <a className="owner-stop-back" href="#">{tr("backToDashboard")}</a>
    </div>
  );
}
